import { t } from "@lingui/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { ClubLogo } from "@/components/ClubLogo/ClubLogo";
import { BaseNavigatorParamList } from "@/navigators/BaseNavigator/types";
import { useFlags } from "@/utils/launchDarkly";
import { ScreenHeader } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenHeader";
import { Button } from "@whoosh/design-system/src/components/controls";
import {
  ArrowLeftIcon,
  PersonOutlineIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { EventsFilterBar } from "../EventsFilterBar";
import { EventsHeaderFragment_club$key } from "./__generated__/EventsHeaderFragment_club.graphql";

export type EventsHeaderProps = {
  isAnonymousCustomer: boolean;
  isRounded: boolean;
  isScrolled: boolean;
  onBackPress?: () => void;
  clubRef: EventsHeaderFragment_club$key | null;
  openFilterModal: () => void;
  currentDepartmentName: string | null;
};

export const EventsHeader = ({
  isAnonymousCustomer,
  isRounded,
  isScrolled,
  onBackPress,
  clubRef,
  openFilterModal,
  currentDepartmentName,
}: EventsHeaderProps) => {
  const club = useFragment(EventsHeaderFragmentString, clubRef);
  const isMobile = Boolean(useResponsiveProp({ base: 1, md: 0 }));

  const navigation = useNavigation<NavigationProp<BaseNavigatorParamList>>();
  const clubSlug = club?.slug ?? "";

  const handleSignIn = () => {
    navigation.navigate("Club", {
      clubSlug,
      screen: "Login",
      params: {
        clubSlug,
      },
    });
  };

  const { newTopbar } = useFlags();
  const eventLabel = getNounOverride(
    club?.copyOverrides?.event?.singular,
    t`Event`
  );

  return (
    <ScreenHeader
      subHeading={newTopbar ? null : club?.name}
      variant={isMobile ? "CONDENSED" : "REGULAR"}
      isRounded={isRounded}
      scrolled={isScrolled}
      leftItem={
        newTopbar ? null : onBackPress ? (
          <Button
            variant="secondary"
            accessibilityLabel={t`Back button`}
            leftIcon={<ArrowLeftIcon />}
            onPress={onBackPress}
            testID="mobile-header-back-button"
          />
        ) : (
          <ClubLogo clubRef={club ?? null} />
        )
      }
      rightItem={
        isAnonymousCustomer ? (
          <Button
            testID="events-header-sign-in-button"
            accessibilityLabel={t`Sign In button`}
            variant="outline"
            leftIcon={<PersonOutlineIcon />}
            onPress={handleSignIn}
          >
            {t`Sign In`}
          </Button>
        ) : null
      }
      titleRightElement={
        isAnonymousCustomer || newTopbar ? null : (
          <ClubLogo clubRef={club ?? null} />
        )
      }
      hideTitleRightElementOnTitleWrap={isMobile}
      title={eventLabel}
      testID="events-header-screen-header"
      bottomComponent={
        isMobile ? (
          <EventsFilterBar
            title={t`Find Events`}
            values={[
              {
                placeholder: t`Department`,
                value: null,
              },
              {
                placeholder: t`Departments`,
                value: currentDepartmentName || t`All`,
              },
            ]}
            onPress={openFilterModal}
          />
        ) : null
      }
    />
  );
};

export const EventsHeaderFragmentString = graphql`
  fragment EventsHeaderFragment_club on Club {
    name
    slug
    copyOverrides {
      event {
        singular
      }
    }
    ...ClubLogo_Club
  }
`;
